import React, { useState, useRef, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Building2, ChevronDown, Check, Plus } from "lucide-react";
import { getOrganizations } from "../serverComm";
import { setActiveOrganization, setOrganizations } from "../../../store/appSlice";

const OrgSwitcher = () => {
  const dispatch = useDispatch();
  const { activeOrganization, organizations } = useSelector((state) => state.app);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const fetchOrgs = useCallback(async () => {
    try {
      const data = await getOrganizations();
      const orgs = data.organizations || [];
      dispatch(setOrganizations(orgs));
      if (!activeOrganization && orgs.length > 0) {
        const savedOrgId = localStorage.getItem("activeOrgId");
        const saved = savedOrgId && orgs.find((o) => o._id === savedOrgId);
        dispatch(setActiveOrganization(saved || orgs[0]));
      }
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
  }, [dispatch, activeOrganization]);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (organizations.length <= 1) return null;

  const handleSwitch = (org) => {
    dispatch(setActiveOrganization(org));
    localStorage.setItem("activeOrgId", org._id);
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (key.startsWith("workspaces_cache_")) {
        localStorage.removeItem(key);
      }
    }
    setIsOpen(false);
    window.location.href = "/workspaces";
  };

  const displayName = activeOrganization?.name || "Personal";

  return (
    <div className="relative hidden md:block" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors max-w-[160px]"
      >
        <Building2 size={14} className="text-gray-500 flex-shrink-0" />
        <span className="truncate font-medium">{displayName}</span>
        <ChevronDown
          size={14}
          className={`text-gray-400 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 animate-dropdown-open">
          <div className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wider">
            Organizations
          </div>
          {organizations.map((org) => (
            <button
              key={org._id}
              onClick={() => handleSwitch(org)}
              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Building2 size={14} className="text-gray-400 flex-shrink-0" />
                <span className="truncate">
                  {org.name || "Personal"}
                </span>
                {org.role && (
                  <span className="text-xs text-gray-400 flex-shrink-0">
                    {org.role}
                  </span>
                )}
              </div>
              {activeOrganization?._id === org._id && (
                <Check size={14} className="text-blue-600 flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrgSwitcher;
